/**
 * Site and finding hooks.
 *
 * The site LIST is deliberately separate from a site's DETAIL. The list is small and
 * fetched on nearly every page (the header selector needs it); the detail carries all 60
 * engine parameters and is only needed when one site is open. Merging them would make
 * the selector's dropdown drag the whole fleet's parameter set with it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SiteParams } from '@/lib/compliance';
import { api, request } from './client';

export type Band = 'pass' | 'warn' | 'fail';

export interface SiteListItem {
  id: string;
  /**
   * Which organisation owns this site.
   *
   * The fleet list spans the caller's reach — their own organisation and every descendant — so a
   * row's owner is no longer implied by the fact that you are looking at it. Rendered only when
   * the reach holds more than one organisation.
   */
  organisationId: string;
  slug: string;
  name: string;
  archetype: string;
  industry: string;
  status: string;
  region: string;
  city: string | null;
  lat: number | null;
  lng: number | null;
  score: number;
  band: Band;
  openFindings: number;
  /**
   * Declared PV array capacity, kWp. **Declared, never measured** — this is what an engineer
   * typed on the Compliance parameters tab, and it is the figure the renewable standards grade.
   *
   * `0` is a site that declares no array, which is a real answer rather than a blank: three IEC
   * 62548 checks read it as "the DC-side requirements do not apply". Render it as absence, never
   * as a missing figure.
   */
  pvArrayKw: number;
  /**
   * A Playground site (0117): its readings are generated, in the demo organisation, through the
   * real ingest. Every surface that names a site says "Simulated" beside it (`SimulatedPill`).
   * Optional because an API older than 0117 does not send it; absent reads as a real site.
   */
  simulated?: boolean;
  updatedAt: string;
}

export interface SiteDetail extends SiteListItem {
  /** Exactly the compliance engine's input, coerced from `numeric` strings server-side. */
  params: SiteParams;
  version: number;
}

/**
 * THE fleet list. There used to be two.
 *
 * `lib/api/queries.ts` exported a second `useSites` with a different cache key and NO
 * limit — so it took the server's default of 50 while this one asks for 200. Two hooks for
 * one question, and the pickers built on the other one (gateway assignment, feed health)
 * would have shown a silently truncated fleet the moment an organisation passed fifty
 * sites, while the header selector two inches away showed all of them. Nothing would have
 * looked broken; a site would simply not have been in the list.
 *
 * 200 is the server's hard maximum (`limit: z.coerce.number().max(200).default(50)`), so
 * this asks for as much as the API can give.
 *
 * `truncated` IS A HEURISTIC AND SAYS SO. The endpoint returns `{ data }` with no total, so
 * a full page is the only signal that there may be more. A fleet of exactly 200 reports
 * truncated when it is not — which errs towards telling the operator to look rather than
 * towards a quietly short list. The exact fix is a `total` on the API response; until then
 * a caller must never present this list as complete without checking the flag.
 */
export const SITES_PAGE_LIMIT = 200;

export function useSites() {
  const query = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.get<{ data: SiteListItem[] }>('/sites', { limit: SITES_PAGE_LIMIT }),
    staleTime: 60_000,
  });
  return {
    ...query,
    truncated: (query.data?.data.length ?? 0) >= SITES_PAGE_LIMIT,
  };
}

export function useSite(id: string | null) {
  return useQuery({
    queryKey: ['site', id],
    queryFn: () => api.get<{ site: SiteDetail }>(`/sites/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * What `PATCH /sites/:id` accepts in its body.
 *
 * Identity fields ride along with the compliance parameters because the backend's
 * `patchSiteSchema` is `identity.partial().merge(siteParamsSchema.partial())` — one
 * endpoint for both. `lat`/`lng` are part of identity, and they are what the fleet map
 * plots from, so a site with no coordinates simply is not on it.
 */
export interface SitePatchBody extends Partial<SiteParams> {
  status?: string;
  name?: string;
  city?: string | null;
  lat?: number | null;
  lng?: number | null;
}

export interface SitePatchVars extends SitePatchBody {
  /**
   * The row version this edit was made against, sent as `If-Match` — NEVER in the body.
   *
   * Optional because the two callers want different things and neither should be forced
   * into the other's failure mode. The location card corrects coordinates nobody else is
   * touching and does not care to lose a race it cannot detect. The compliance-parameter
   * editor rewrites values that decide verdicts, where two engineers surveying the same
   * site silently overwriting each other is a real and expensive outcome — so it sends the
   * version, and the server answers 409 rather than accepting the later write.
   *
   * Stripped from the payload below: `patchSiteSchema` is a Zod object over real columns
   * and an unknown key in the body is not a field the API has.
   */
  ifMatch?: number | undefined;
}

export function usePatchSite(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ifMatch, ...patch }: SitePatchVars) =>
      request<{ site: SiteDetail; version: number }>(`/sites/${id}`, {
        method: 'PATCH',
        body: patch,
        ...(ifMatch === undefined ? {} : { headers: { 'If-Match': String(ifMatch) } }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['site', id] });
      void qc.invalidateQueries({ queryKey: ['sites'] });
      // Coordinates move the marker, and the fleet map reads the site LIST.
      void qc.invalidateQueries({ queryKey: ['site-health'] });
      // A parameter change re-derives findings, so the queue and its badge are stale.
      void qc.invalidateQueries({ queryKey: ['findings'] });
      void qc.invalidateQueries({ queryKey: ['findings-summary'] });
    },
  });
}

/**
 * `DELETE /sites/:id` is a SOFT delete server-side: the row keeps its telemetry, audit
 * trail and evaluations, ingestion stops resolving the site's payloads, and every list
 * stops returning it. Nothing in the UI can reinstate it, so the caller owns the
 * confirmation ceremony — this hook assumes the decision is already made.
 */
export function useDeleteSite(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<null>(`/sites/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] });
      // The map plots from site health, and this marker must disappear with the site.
      void qc.invalidateQueries({ queryKey: ['site-health'] });
      // Its findings leave the queue and the badge count with it.
      void qc.invalidateQueries({ queryKey: ['findings'] });
      void qc.invalidateQueries({ queryKey: ['findings-summary'] });
      /* ['site', id] is deliberately NOT removed here. The hub still observes it in the
         tick before navigation, and removing an observed query makes TanStack refetch it
         — straight into a 404 for a row that no longer resolves. It ages out with GC. */
    },
  });
}

/* ---------------------------------------------------------------- findings */

export type FindingStatus = 'open' | 'in-progress' | 'resolved' | 'accepted-risk';

export interface Finding {
  key: string;
  standardId: string;
  standardName: string;
  domain: string;
  checkId: string;
  title: string;
  detail: string;
  clause: string;
  severity: 'high' | 'medium';
  siteId: string;
  siteName: string;
  siteArchetype: string;
  siteIndustry: string;
  status: FindingStatus;
  ownerId: string | null;
  ownerEmail: string | null;
  dueDate: string | null;
  note: string | null;
  overdue: boolean;
}

/**
 * Every field is `| undefined` explicitly.
 *
 * Under `exactOptionalPropertyTypes` clearing a filter by assigning `undefined` is a
 * different type from omitting the key, and "clear this filter" is exactly what the UI
 * does — so `domain?: string` would reject `set({ domain: undefined })`.
 */
export interface FindingFilters {
  siteId?: string | undefined;
  domain?: string | undefined;
  severity?: 'high' | 'medium' | undefined;
  status?: FindingStatus | 'unresolved' | undefined;
  q?: string | undefined;
}

export function useFindings(filters: FindingFilters = {}) {
  return useQuery({
    queryKey: ['findings', filters],
    queryFn: () =>
      api.get<{ data: Finding[]; total: number; truncated: boolean }>('/findings', {
        ...filters,
        limit: 300,
      }),
    staleTime: 30_000,
  });
}

/**
 * The shape `/findings/summary` returns. Exported so the rail badge, the Findings band and
 * the header's `FindingsButton` popover can all type the same object rather than each
 * declaring their own inline slice of it.
 */
export interface FindingsSummary {
  unresolved: number;
  high: number;
  overdue: number;
  byStatus: Record<string, number>;
  byDomain: Record<string, number>;
}

/**
 * The findings counts, for the rail badge, the Findings band and the header's Findings
 * popover.
 *
 * Takes the site, because the badge and the page it opens must agree: the badge used to
 * count the whole fleet while the page could be scoped to one site, and a "7" that opens a
 * list of two teaches an operator to distrust the number. Keyed on the site so switching the
 * header refetches rather than reuses; the `['findings-summary']` prefix still matches
 * `usePatchFinding`'s invalidation.
 *
 * `enabled` (0083, default `true`): the API now gates this read on `findings.read`, and the
 * callers that show its number OUTSIDE the Findings screen itself — the rail badge and the
 * header's `FindingsButton`, both in `App.tsx` — must quietly omit it without the permission
 * rather than let the request 403. `App.tsx` passes `held.has('findings.read')`.
 */
export function useFindingsSummary(siteId?: string, enabled = true) {
  return useQuery({
    queryKey: ['findings-summary', siteId ?? 'fleet'],
    queryFn: () => api.get<FindingsSummary>('/findings/summary', siteId ? { siteId } : undefined),
    enabled,
    staleTime: 30_000,
  });
}

export function usePatchFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      key: string;
      status?: FindingStatus;
      dueDate?: string | null;
      note?: string | null;
      ownerId?: string | null;
    }) => api.patch<{ finding: Finding }>('/findings', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['findings'] });
      void qc.invalidateQueries({ queryKey: ['findings-summary'] });
    },
  });
}
